<template>
  <div class="sv">
    <!-- Linke Spalte: Liste -->
    <aside class="sv__list">
      <div class="sv__listHead">
        <div class="sv__views">
          <button v-for="v in views" :key="v.id" class="sv__view" :class="{ 'sv__view--active': view === v.id }" @click="view = v.id">
            {{ v.label }}<span v-if="counts[v.id]" class="sv__count">{{ counts[v.id] }}</span>
          </button>
        </div>
        <v-text-field v-model="search" dense outlined rounded hide-details clearable placeholder="Bestellung, E-Mail, Betreff…" prepend-inner-icon="mdi-magnify" class="sv__search" />
        <div class="sv__filters">
          <v-select v-model="sort" :items="sortOptions" dense outlined rounded hide-details class="sv__select" />
          <v-select v-model="brand" :items="brandOptions" dense outlined rounded hide-details class="sv__select" clearable placeholder="Alle Marken" />
          <v-btn icon small :color="unreadOnly ? '#7b5cd6' : undefined" title="Nur ungelesen" @click="unreadOnly = !unreadOnly"><v-icon small>mdi-email-alert-outline</v-icon></v-btn>
          <v-btn icon small :loading="syncing" title="Mit Quelp abgleichen (offene Tickets)" @click="sync"><v-icon small>mdi-cloud-sync-outline</v-icon></v-btn>
        </div>
      </div>
      <div class="sv__items">
        <div v-if="loadingTickets && !items.length" class="sv__muted"><v-progress-circular indeterminate size="16" width="2" color="#7b5cd6" class="mr-2" />Tickets laden…</div>
        <div v-else-if="!items.length && !noOrderLoading" class="sv__muted">Keine Tickets in dieser Ansicht.</div>
        <div v-if="view === 'noorder' && noOrderLoading" class="sv__muted"><v-progress-circular indeterminate size="16" width="2" color="#7b5cd6" class="mr-2" />Tickets ohne Bestellung laden…</div>
        <div
          v-for="t in items"
          :key="t.orderUid"
          class="sv__item"
          :class="{ 'sv__item--active': selected && selected.orderUid === t.orderUid, 'sv__item--unread': t.quelpUnread, 'sv__item--express': t.order && t.order.isExpress }"
          @click="select(t)"
        >
          <div class="sv__itemTop">
            <span class="sv__flag">{{ flagOf(t) }}</span>
            <b class="sv__orderName">{{ t.orderName || (t.noOrder ? customerOf(t) : t.orderUid) }}</b>
            <span v-if="t.order && t.order.isExpress" class="sv__chip sv__chip--express"><v-icon x-small color="#b26a00">mdi-flash</v-icon>EXPRESS</span>
            <span v-if="t.quelpPriority === 'high'" class="sv__chip sv__chip--prio"><v-icon x-small color="#c62828">mdi-fire</v-icon></span>
            <span class="sv__spacer"></span>
            <span class="sv__state" :class="'sv__state--' + stateOf(t)">{{ stateLabel(t) }}</span>
          </div>
          <div class="sv__subject">{{ subjectOf(t) }}</div>
          <div class="sv__preview">{{ previewOf(t) }}</div>
          <div class="sv__itemMeta">
            <span :title="'Letzte Nachricht'"><v-icon x-small>mdi-message-outline</v-icon> {{ ago(lastMessageAt(t)) }}</span>
            <span :title="'Ticket-Alter'"><v-icon x-small>mdi-clock-outline</v-icon> {{ ago(ticketCreatedAt(t)) }}</span>
            <span v-if="t.order && t.order.orderDate" :title="'Bestell-Alter'"><v-icon x-small>mdi-cart-outline</v-icon> {{ ago(t.order.orderDate) }}</span>
            <span v-if="t.order && t.order.taskCount"><v-icon x-small>mdi-brush</v-icon> {{ t.order.taskCount }}</span>
            <span v-if="brandOf(t)" class="sv__brand">{{ brandOf(t) }}</span>
          </div>
        </div>
        <v-btn v-if="view === 'noorder' && noOrderCursor" text small block color="#6a4fc4" :loading="noOrderLoading" @click="loadNoOrder(true)">Mehr laden</v-btn>
      </div>
    </aside>

    <!-- Mitte: Bestellung + Tasks -->
    <section class="sv__center">
      <SupportOrderTasks v-if="selected" :ticket="selected" />
      <div v-else class="sv__empty"><v-icon large color="rgba(0,0,0,.25)">mdi-headset</v-icon><div>Ticket auswählen</div></div>
    </section>

    <!-- Rechts: Verlauf + Antwort -->
    <section class="sv__right">
      <QuelpThread v-if="selected" :ticket="selected" @ticket-updated="onTicketUpdated" @notice="notice" @open-order="openOrder" @sent="onSent" />
    </section>
    <SendStatusPills @open="(j) => j.orderUid && openOrder(j.orderUid)" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../fb";
import { countryFlagEmoji, getDateFromNowShort } from "@/utils";
import SupportOrderTasks from "@/components/Support/SupportOrderTasks";
import QuelpThread from "@/components/Support/QuelpThread";
import SendStatusPills from "@/components/Support/SendStatusPills";
import { EventBus, events } from "../../event-bus";

// Support-Ansicht: links Tickets (Quelp-Spiegel), Mitte Bestellung + Tasks,
// rechts Verlauf + Antwort. EXPRESS immer oben, dann Priorität, dann Sortierung.
export default {
  name: "SupportView",
  components: { SupportOrderTasks, QuelpThread, SendStatusPills },
  data() {
    return {
      view: "open",
      search: "",
      sort: "lastMessage",
      brand: null,
      unreadOnly: false,
      selectedUid: null,
      syncing: false,
      views: [
        { id: "open", label: "Offen" },
        { id: "hold", label: "On Hold" },
        { id: "active", label: "Alle aktiven" },
        { id: "noorder", label: "Ohne Bestellung" },
        { id: "closed", label: "Erledigt" },
      ],
      noOrderItems: [],
      noOrderLoading: false,
      noOrderCursor: null,
      sortOptions: [
        { text: "Letzte Nachricht", value: "lastMessage" },
        { text: "Älteste Tickets zuerst", value: "ticketAge" },
        { text: "Älteste Bestellung zuerst", value: "orderAge" },
        { text: "Neueste Bestellung zuerst", value: "orderNew" },
      ],
    };
  },
  computed: {
    ...mapGetters(["ticketsActive", "ticketsClosed", "loadingTickets"]),
    pool() {
      if (this.view === "noorder") return this.noOrderItems;
      if (this.view === "closed") return this.ticketsClosed;
      if (this.view === "open") return this.ticketsActive.filter((t) => !t.waiting);
      if (this.view === "hold") return this.ticketsActive.filter((t) => t.quelpOnHold || t.waiting);
      return this.ticketsActive;
    },
    counts() {
      const a = this.ticketsActive;
      return {
        open: a.filter((t) => !t.waiting).length,
        hold: a.filter((t) => t.quelpOnHold || t.waiting).length,
        active: a.length,
        noorder: this.noOrderItems.length,
        closed: 0,
      };
    },
    brandOptions() {
      const set = new Set();
      this.ticketsActive.forEach((t) => {
        const b = this.brandOf(t);
        if (b) set.add(b);
      });
      return [...set].sort();
    },
    items() {
      const q = (this.search || "").trim().toLowerCase();
      let list = this.pool.filter((t) => {
        if (this.unreadOnly && !t.quelpUnread) return false;
        if (this.brand && this.brandOf(t) !== this.brand) return false;
        if (!q) return true;
        const hay = [t.orderName, t.orderUid, t.email, this.subjectOf(t), this.previewOf(t), t.order && t.order.customerName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
      const key = (t) => {
        switch (this.sort) {
          case "ticketAge":
            return String(this.ticketCreatedAt(t) || "");
          case "orderAge":
            return String((t.order && t.order.orderDate) || "9");
          case "orderNew":
            return "-" + String((t.order && t.order.orderDate) || "");
          default:
            return "-" + String(this.lastMessageAt(t) || "");
        }
      };
      const cmp = (a, b) => {
        const ea = a.order && a.order.isExpress ? 1 : 0;
        const eb = b.order && b.order.isExpress ? 1 : 0;
        if (ea !== eb) return eb - ea;
        const pa = a.quelpPriority === "high" ? 1 : 0;
        const pb = b.quelpPriority === "high" ? 1 : 0;
        if (pa !== pb) return pb - pa;
        const ka = key(a);
        const kb = key(b);
        // "-"-Präfix = absteigend
        if (ka.startsWith("-") || kb.startsWith("-")) return kb.localeCompare(ka);
        return ka.localeCompare(kb);
      };
      list = [...list].sort(cmp);
      return list;
    },
    selected() {
      if (!this.selectedUid) return null;
      return (
        this.ticketsActive.find((t) => String(t.orderUid) === String(this.selectedUid)) ||
        this.ticketsClosed.find((t) => String(t.orderUid) === String(this.selectedUid)) ||
        this.noOrderItems.find((t) => String(t.orderUid) === String(this.selectedUid)) ||
        null
      );
    },
  },
  watch: {
    view(v) {
      if (v === "closed") this.loadTickets("CLOSED");
      if (v === "noorder" && !this.noOrderItems.length) this.loadNoOrder();
    },
    "$route.query.order": {
      immediate: true,
      handler(v) {
        if (v) this.selectedUid = String(v);
      },
    },
  },
  mounted() {
    this.loadTickets("OPEN");
    if (this.view === "closed") this.loadTickets("CLOSED");
  },
  methods: {
    ...mapActions(["loadTickets"]),
    select(t) {
      this.selectedUid = String(t.orderUid);
      if (String(this.$route.query.order || "") !== this.selectedUid) {
        this.$router.replace({ query: { ...this.$route.query, order: this.selectedUid } }).catch(() => {});
      }
    },
    ago(d) {
      return d ? getDateFromNowShort(d) : "–";
    },
    activeTicket(t) {
      const map = t.quelpTickets || {};
      return (t.quelpId && map[t.quelpId]) || Object.values(map)[0] || null;
    },
    stateOf(t) {
      const q = this.activeTicket(t);
      if (!t.pending) return "done";
      if (q) return q.status === "open" ? "open" : q.onHold ? "hold" : "done";
      return t.waiting ? "hold" : "open";
    },
    stateLabel(t) {
      const s = this.stateOf(t);
      return s === "open" ? "Offen" : s === "hold" ? "Hold" : "Beantwortet";
    },
    subjectOf(t) {
      const q = this.activeTicket(t);
      if (q) return q.subject || "Ohne Betreff";
      const keys = Object.keys(t.data || {});
      return keys.length ? `Status-Seite · ${keys.join(", ")}` : "Ticket";
    },
    previewOf(t) {
      const q = this.activeTicket(t);
      if (q && q.lastMessage) return `${q.lastMessage.direction === "outbound" ? "↩ " : ""}${q.lastMessage.preview || ""}`;
      const legacy = Object.values(t.data || {}).flatMap((x) => (x && x.messages) || []);
      const last = legacy[legacy.length - 1];
      return last ? last.message : "";
    },
    lastMessageAt(t) {
      const q = this.activeTicket(t);
      return t.quelpLastMessageAt || (q && q.lastMessage && q.lastMessage.date) || t.created;
    },
    ticketCreatedAt(t) {
      const q = this.activeTicket(t);
      return (q && q.receivedAt) || t.created;
    },
    brandOf(t) {
      const q = this.activeTicket(t);
      return (q && q.brand && q.brand.alias) || (t.order && t.order.shopId ? `${t.order.shopId}-${t.order.countryCode || ""}` : "");
    },
    customerOf(t) {
      const q = this.activeTicket(t);
      return (q && q.customer && (q.customer.name || q.customer.email)) || t.email || "Ohne Bestellung";
    },
    flagOf(t) {
      const cc = t.order && (t.order.shippingCountryCode || t.order.countryCode);
      return cc ? countryFlagEmoji(cc) : "";
    },
    // Tickets ohne Bestellung liegen nicht im Spiegel → direkt aus der Quelp-API.
    // Darstellung als support-ähnliches Objekt, damit Liste/Thread gleich bleiben.
    wrapNoOrder(t) {
      return {
        orderUid: `q_${t.id}`,
        noOrder: true,
        orderName: null,
        email: (t.customer && t.customer.email) || "",
        created: t.receivedAt,
        pending: t.status === "open",
        waiting: false,
        quelpId: t.id,
        quelpTickets: { [t.id]: t },
        quelpUnread: t.status === "open" && t.read === false,
        quelpOnHold: !!t.onHold,
        quelpPriority: t.priority || "normal",
        quelpLastMessageAt: (t.lastMessage && t.lastMessage.date) || t.updatedAt || t.receivedAt,
        order: null,
      };
    },
    async loadNoOrder(more = false) {
      this.noOrderLoading = true;
      try {
        const fn = httpsCallable(functions, "quelpAction", { timeout: 70000 });
        const res = await fn({ action: "listNoOrder", status: "open", cursor: more ? this.noOrderCursor : null });
        if (!res.data || res.data.status !== "ok") throw new Error((res.data && res.data.message) || "Laden fehlgeschlagen");
        const items = (res.data.items || []).map(this.wrapNoOrder);
        this.noOrderItems = more ? [...this.noOrderItems, ...items] : items;
        this.noOrderCursor = res.data.nextCursor || null;
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: e.message, type: "error" });
      } finally {
        this.noOrderLoading = false;
      }
    },
    onTicketUpdated(t) {
      if (!t || !t.id) return;
      const idx = this.noOrderItems.findIndex((x) => x.quelpId === t.id);
      if (idx === -1) return;
      if (t.order && t.order.id) {
        // Jetzt mit Bestellung → kommt über den Spiegel, hier raus.
        this.noOrderItems.splice(idx, 1);
        this.selectedUid = String(t.order.id);
        return;
      }
      const wrapped = this.wrapNoOrder(t);
      if (t.status !== "open") this.noOrderItems.splice(idx, 1);
      else this.noOrderItems.splice(idx, 1, wrapped);
    },
    notice(msg) {
      EventBus.$emit(events.SNACKBAR, { message: msg, type: "success" });
    },
    openOrder(orderUid) {
      this.selectedUid = String(orderUid);
      this.$router.replace({ query: { ...this.$route.query, order: String(orderUid) } }).catch(() => {});
    },
    // Nach dem Absenden (läuft im Hintergrund) direkt zum nächsten Ticket der Liste.
    onSent() {
      const list = this.items;
      const idx = list.findIndex((t) => String(t.orderUid) === String(this.selectedUid));
      const next = list[idx + 1] || list[idx - 1] || null;
      if (next && String(next.orderUid) !== String(this.selectedUid)) this.select(next);
    },
    async sync() {
      this.syncing = true;
      try {
        const fn = httpsCallable(functions, "quelpAction", { timeout: 70000 });
        const res = await fn({ action: "sync", status: "open", limit: 200 });
        if (res.data && res.data.status === "ok") {
          EventBus.$emit(events.SNACKBAR, { message: `Quelp-Abgleich: ${res.data.mirrored} von ${res.data.total} offenen Tickets mit Bestellung übernommen`, type: "success" });
        } else {
          throw new Error((res.data && res.data.message) || "Abgleich fehlgeschlagen");
        }
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: e.message, type: "error" });
      } finally {
        this.syncing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sv {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr) minmax(380px, 1.1fr);
  gap: 14px;
  padding: 12px;
  height: calc(100vh - 88px);
  min-height: 560px;
  @media (max-width: 1280px) {
    grid-template-columns: 320px minmax(0, 1fr);
    grid-template-rows: auto auto;
    height: auto;
    .sv__right { grid-column: 1 / -1; min-height: 520px; }
  }
  &__list {
    display: flex; flex-direction: column; min-height: 0;
    background: #fff; border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  }
  &__listHead { padding: 12px 12px 8px; display: flex; flex-direction: column; gap: 8px; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
  &__views { display: flex; gap: 4px; flex-wrap: wrap; }
  &__view {
    border: 0; background: #f1f0f5; color: rgba(0, 0, 0, 0.6); font-weight: 700; font-size: 12px; border-radius: 999px; padding: 5px 11px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    &--active { background: #edeafa; color: #6a4fc4; }
  }
  &__count { background: rgba(0, 0, 0, 0.08); border-radius: 999px; padding: 0 6px; font-size: 10px; }
  &__view--active .sv__count { background: rgba(106, 79, 196, 0.15); }
  &__search { font-size: 13px; }
  &__filters { display: flex; align-items: center; gap: 6px; }
  &__select { font-size: 12px; max-width: 170px; }
  &__items { flex: 1 1 auto; overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 6px; }
  &__muted { font-size: 12px; color: rgba(0, 0, 0, 0.5); padding: 12px; display: flex; align-items: center; }
  &__item {
    border-radius: 12px; padding: 10px 12px; cursor: pointer; background: #fafafa; border: 1px solid transparent; display: flex; flex-direction: column; gap: 3px;
    &:hover { background: #f4f2fb; }
    &--active { background: #edeafa; border-color: rgba(123, 92, 214, 0.35); }
    &--unread { box-shadow: inset 4px 0 0 #7b5cd6; }
    &--express { background: #fff8ee; }
    &--express.sv__item--active { background: #f6ecf7; }
  }
  &__itemTop { display: flex; align-items: center; gap: 6px; min-width: 0; }
  &__flag { font-size: 15px; }
  &__orderName { font-size: 13px; color: #282828; white-space: nowrap; }
  &__spacer { flex: 1 1 auto; }
  &__chip { display: inline-flex; align-items: center; gap: 2px; font-size: 10px; font-weight: 700; border-radius: 999px; padding: 0 7px; background: #edeafa; color: #6a4fc4;
    &--express { background: #fff3e0; color: #b26a00; } &--prio { background: #ffebee; color: #c62828; padding: 0 4px; } }
  &__state { font-weight: 700; font-size: 10px; border-radius: 999px; padding: 1px 8px; white-space: nowrap;
    &--open { background: #ffebee; color: #c62828; } &--hold { background: #fff3e0; color: #b26a00; } &--done { background: #e8f5e9; color: #2e7d32; } }
  &__subject { font-size: 12.5px; font-weight: 600; color: #282828; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__preview { font-size: 12px; color: rgba(0, 0, 0, 0.55); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__itemMeta { display: flex; gap: 10px; flex-wrap: wrap; font-size: 11px; color: rgba(0, 0, 0, 0.45); margin-top: 2px; align-items: center;
    .v-icon { font-size: 13px !important; color: rgba(0, 0, 0, 0.35); margin-right: 1px; } }
  &__brand { margin-left: auto; font-size: 10px; background: #f1f0f5; border-radius: 999px; padding: 0 7px; }
  &__center { min-height: 0; overflow-y: auto; }
  &__right { min-height: 0; display: flex; flex-direction: column; }
  &__empty { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: rgba(0, 0, 0, 0.45); font-size: 13px; }
}
</style>
